"use client";

import { ExternalLinkIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Link,
  SimpleGrid,
  Text,
  Image,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import ProgressLike from "./ProgressLike";
import avatar from "../../images/Avatar.png";
import Insta from "../../images/insta_logo.png";
import Facebook from "../../images/facebook_logo.png";
import Youtube from "../../images/youtube_logo.png";
import tiktok from "../../images/tictok_logo.png";

const BRAND = {
  blue: "#0054C0",
  textPrimary: "#172940",
  textSecondary: "#5D6979",
  cardBg: "white",
};
const DAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

const PLATFORM_ICONS = {
  instagram: Insta,
  youtube: Youtube,
  tiktok: tiktok,
  facebook: Facebook,
};

export function makeWeeklyChartData(item) {
  const weekly = item?.insight?.weeklyTrend ?? [];
  const billing = String(item?.target?.billingModel ?? "").toUpperCase();

  return weekly.map((w) => ({
    name: DAY_LABELS[dayjs(w.date).day()],
    value:
      billing === "CPS"
        ? (w.quantity ?? w.totalQuantity ?? w.interactions ?? w.salesCount ?? w.sales ?? w.orders ?? 0)
        : billing === "CPUC"
          ? (w.interactions ?? 0)
          : (w.reach ?? 0),
  }));
}

function formatCurrency(value, currency) {
  const n = Number(value ?? 0);
  if (Number.isNaN(n)) return "-";
  if (String(currency ?? "KRW").toUpperCase() === "KRW") {
    return `₩${Math.round(n).toLocaleString()}`;
  }
  return `${Math.round(n).toLocaleString()}${currency ?? ""}`;
}

export default function CreatorInfo({ data, campaign }) {
  const percent =
    (data.insight.goalProgress.currentValue /
      data.insight.goalProgress.targetValue) *
    100;
  const isCps = String(data?.target?.billingModel ?? "").toUpperCase() === "CPS";
  const commissionRate = Number(data?.target?.unitPrice ?? 0) * 100;
  const salesCount = Number(data?.insight?.goalProgress?.currentValue ?? 0);
  const totalClicks = Number(data?.insight?.totalClicks ?? 0);
  const totalQuantity = Number(data?.insight?.totalQuantity ?? 0);
  const roas = Number(data?.insight?.roas ?? 0);
  const manuscriptFee = Number(data?.insight?.manuscriptFee ?? 0);
  const expectedCommission = Number(data?.insight?.expectedCommission ?? 0);
  const promoLinks = Array.isArray(data?.promoLinks) ? data.promoLinks : [];
  const datas = makeWeeklyChartData(data);
  const copyPromoLink = async (shareUrl) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    }
    catch {
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  return (
    <Flex
      w="100%"
      align="stretch"
      bg={BRAND.cardBg}
      p="12px"
      gap="12px"
      flexDir="column"
      borderRadius="16px"
      boxShadow="0px 4px 12px rgba(23, 41, 64, 0.20)"
      position="relative"
    >
      {/* ================= 상단 타이틀 + 버튼 ================ */}
      <Flex
        justify={{ base: "flex-start", md: "space-between" }}
        align={{ base: "flex-start", md: "center" }}
        flexDir={{ base: "column", md: "row" }}
        gap={{ base: "10px", md: "0px" }}
      >
        <Text fontSize="20px" fontWeight={700}>
          {campaign?.campaign?.title}
        </Text>
      </Flex>

      {/* ================= 상단: 좌측 정보 + 우측 차트 ================= */}
      <Flex gap="12px" flexDir={{ base: "column", md: "row" }} w="100%">
        {/* ============ 좌측 ============ */}
        <Flex w={{ base: "100%", md: "50%" }} flexDir="column" gap="12px">
          {/* 광고주 */}
          <Flex
            w="100%"
            flexDir="column"
            p="8px"
            border="1px solid #D1D4D9"
            borderRadius="8px"
            gap="2px"
          >
            <Text color={BRAND.textSecondary} fontSize="12px" fontWeight={500}>
              크리에이터
            </Text>
            <Flex gap="12px" align="center">
              <Image src={data.creator.avatarUrl ?? avatar} w="40px" h="40px" />
              <Text fontSize="20px" fontWeight={700}>
                {data.creator.username}
              </Text>
              <Image
                w="24px"
                h="24px"
                src={
                  PLATFORM_ICONS[data.creator.channelPlatform?.toLowerCase()] ??
                  "/images/default.png"
                }
                alt="platform-icon"
              />
            </Flex>
          </Flex>

          <Flex
            w="100%"
            flexDir="column"
            p="8px"
            gap="2px"
            border="1px solid #D1D4D9"
            borderRadius="8px"
          >
            <Text color="#5D6979" fontSize="12px" fontWeight={500}>
              라이브 기간
            </Text>
            <Text fontSize="16px" fontWeight={600}>
              {data.liveSchedule.startAt?.split("T")[0]} ~{" "}
              {data.liveSchedule.endAt?.split("T")[0]}
            </Text>
          </Flex>

          <Flex gap="12px">
            {!isCps && (
              <Flex
                w="100%"
                flexDir="column"
                p="8px"
                gap="2px"
                border="1px solid #D1D4D9"
                borderRadius="8px"
              >
                <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                  목표 KPI
                </Text>
                <Text fontSize="16px" fontWeight={600}>
                  {data.target.targetValue}회{" "}
                  {data.target.billingModel === "CPUC"
                    ? "고유 클릭"
                    : "고유 노출"}
                </Text>
              </Flex>
            )}
            <Flex
              w="100%"
              flexDir="column"
              p="8px"
              gap="2px"
              border="1px solid #D1D4D9"
              borderRadius="8px"
            >
              <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                {isCps ? "커미션율" : "KPI 단가"}
              </Text>
              <Text fontSize="16px" fontWeight={600}>
                {isCps
                  ? `${String(Math.round(commissionRate * 100) / 100).replace(/\.00$/, "")}%`
                  : `${data.target.unitPrice}${data.target.currency}`}
              </Text>
            </Flex>
          </Flex>
          {isCps && (
            <Text fontSize="12px" color="#5D6979" px="2px">
              판매 발생 시 커미션이 집계됩니다.
            </Text>
          )}
          {isCps ? (
            <Flex
              w="100%"
              flexDir="column"
              p="8px"
              border="1px solid #D1D4D9"
              borderRadius="8px"
              gap="8px"
            >
              <Text color={BRAND.textSecondary} fontSize="12px" fontWeight={500}>
                CPS 상품 판매링크
              </Text>
              {promoLinks.length > 0 ? (
                promoLinks.map((link) => {
                  const shareUrl = String(link?.shareUrl ?? "").trim();
                  return (
                    <Flex
                      key={`${link?.cpsProductId}-${link?.creatorProductLinkId ?? "pending"}`}
                      gap="8px"
                      align="center"
                      justify="space-between"
                      flexWrap="wrap"
                    >
                      <Box minW="0" flex="1">
                        <Text fontSize="13px" fontWeight={700} color={BRAND.textPrimary} noOfLines={1}>
                          {link?.productName ?? "CPS 상품"}
                        </Text>
                        <Text
                          fontSize="12px"
                          color={shareUrl ? BRAND.textSecondary : "gray.400"}
                          noOfLines={1}
                        >
                          {shareUrl || "홍보 링크 미발급"}
                        </Text>
                      </Box>
                      {shareUrl && (
                        <HStack spacing="6px" flexShrink={0}>
                          <Button
                            size="xs"
                            variant="outline"
                            borderColor="#C7D7F2"
                            color={BRAND.blue}
                            onClick={() => copyPromoLink(shareUrl)}
                          >
                            복사
                          </Button>
                          <Button
                            size="xs"
                            bg={BRAND.blue}
                            color="white"
                            _hover={{ bg: "#003f9a" }}
                            onClick={() => window.open(shareUrl, "_blank", "noopener,noreferrer")}
                          >
                            열기
                          </Button>
                        </HStack>
                      )}
                    </Flex>
                  );
                })
              ) : (
                <Text fontSize="12px" color="gray.400">
                  발급된 판매링크가 없습니다.
                </Text>
              )}
            </Flex>
          ) : (
            <>
              {/* 연결된 콘텐츠 */}
              <Flex
                w="100%"
                flexDir="column"
                p="8px"
                border="1px solid #D1D4D9"
                borderRadius="8px"
                gap="2px"
                overflowX="auto"
              >
                <Text color={BRAND.textSecondary} fontSize="12px" fontWeight={500}>
                  연결된 콘텐츠
                </Text>

                <Flex gap="4px" align="center">
                  <ExternalLinkIcon color="blue.600" />
                  <Link
                    href={data?.connectedPost?.permalink}
                    isExternal
                    fontSize="16px"
                    fontWeight={600}
                >
                    {data?.connectedPost?.permalink}
                  </Link>
                </Flex>
              </Flex>

              <Flex
                w="100%"
                flexDir="column"
                p="8px"
                gap="2px"
                border="1px solid #D1D4D9"
                borderRadius="8px"
              >
                <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                  최근 업데이트
                </Text>
                <Text fontSize="16px" fontWeight={600}>
                  {data.insight.lastFetchedAt?.split("T")[0] ?? "-"}
                </Text>
              </Flex>
            </>
          )}
        </Flex>

        {/* ============ 우측 ============ */}
        <Flex w={{ base: "100%", md: "50%" }} flexDir="column" gap="20px">
          {/* ========== 차트 카드 ========== */}
          <Box
            w="100%"
            h={isCps ? "320px" : "70%"}
            p="12px"
            border="1px solid #D1D4D9"
            borderRadius="8px"
            bg="white"
          >
            <Text fontSize="14px" color={BRAND.textSecondary} mb="8px">
              성과 분석
            </Text>

            <Box w="100%" h="160px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={datas}>
                  <CartesianGrid
                    stroke="#E2EEFF"
                    strokeDasharray="3 3"
                    vertical={false}
                  />
                  <XAxis dataKey="name" stroke="#8A8F99" fontSize={10} />
                  <YAxis
                    stroke="#8A8F99"
                    fontSize={10}
                    axisLine={false}
                    tickMargin={4}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#E2EEFF",
                      borderRadius: "8px",
                      border: "none",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2864FF"
                    strokeWidth={2}
                    dot={{ r: 2, fill: "#2864FF" }}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>

            <Flex justify="space-between" align="center" mt="12px">
              <HStack spacing="4px" align="center">
                <Box boxSize="8px" borderRadius="full" bg="#0054C0" />
                <Text fontSize="12px" fontWeight={500} color="#0054C0">
                  {isCps
                    ? "판매수량"
                    : data.target.billingModel === "CPUC"
                    ? "고유 클릭 수"
                    : "고유 노출 수"}
                </Text>
              </HStack>

              <IconButton
                aria-label="확대 보기"
                icon={<SearchIcon />}
                w="30px"
                h="30px"
                borderRadius="full"
                bg="#EDF2F7"
                color="#0054C0"
                _hover={{ bg: "#D1D4D9" }}
              />
            </Flex>
          </Box>
          {isCps && (
            <Flex
              w="100%"
              flexDir="column"
              p="8px"
              gap="2px"
              border="1px solid #D1D4D9"
              borderRadius="8px"
              bg="white"
            >
              <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                최근 업데이트
              </Text>
              <Text fontSize="16px" fontWeight={600}>
                {data.insight.lastFetchedAt?.split("T")[0] ?? "-"}
              </Text>
            </Flex>
          )}

          {!isCps && (
            <Flex
              h="30%"
              flexDir="column"
              gap="6px"
              border="1px solid #D1D4D9"
              borderRadius="8px"
              p="12px"
            >
              <Flex justify="space-between">
                <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                  목표 달성률
                </Text>
                <Flex align="center">
                  <Text color="#0054C0" fontSize="14px" fontWeight={700}>
                    {data.insight.goalProgress.currentValue}
                  </Text>
                  <Text color="#5D6979" fontSize="12px" fontWeight={500}>
                    /{data.insight.goalProgress.targetValue}
                  </Text>
                </Flex>
              </Flex>
              <ProgressLike percent={percent} />
              <Flex justify="space-between" align="center">
                <Text color="#5D6979" fontSize="12px" fontWeight={500}>예상 비용</Text>
                <Text color="#0054C0" fontSize="16px" fontWeight={700}>
                  {`${data.insight.expectedCost.toLocaleString()}${data.target.currency}`}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      {isCps && (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={0} mt={2} pt={3} borderTop="1px solid" borderColor="gray.100">
          {[
            { label: "클릭수", value: `${totalClicks.toLocaleString()}회` },
            { label: "판매수", value: `${salesCount.toLocaleString()}건` },
            { label: "판매수량", value: `${totalQuantity.toLocaleString()}개` },
            { label: "ROAS", value: `${roas.toFixed(1)}%` },
            { label: "원고료", value: formatCurrency(manuscriptFee, data?.target?.currency) },
            { label: "커미션", value: formatCurrency(expectedCommission, data?.target?.currency) },
          ].map((metric, idx, arr) => (
            <Box
              key={metric.label}
              px={3}
              py={1}
              borderRightWidth={idx === arr.length - 1 ? "0" : "1px"}
              borderColor="gray.100"
            >
              <Text fontSize="xs" color="gray.500" fontWeight={500}>
                {metric.label}
              </Text>
              <Text color="blue.500" fontSize="15px" fontWeight={700} whiteSpace="nowrap">
                {metric.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Flex>
  );
}
